package com.essntl.features.proposal.domain.model

import com.essntl.features.currency.domain.model.CurrencyModel
import com.essntl.features.service.domain.model.service.ServiceModel
import com.essntl.features.supplier.domain.model.SupplierModel
import core.dateformat.utils.formatTimestampToHHMM

data class ProposalServiceModel(
    val id: String,
    val proposalId: String?,
    val service: ServiceModel,
    val grouped: Boolean?,
    val additionalInfo: String?,
    val editedDescription: String?,
    val startDate: String,
    val endDate: String?,
    val startTime: String?,
    val endTime: String?,
    val index: Int,
    val status: Status?,
    val createdAt: String,
    val updatedAt: String?,
    val price: Int?,
    val cost: Int?,
    val internalNotes: String?,
    val supplier: SupplierModel?,
    val guestCount: Int,
    val updatedBy: String?,
    val clients: List<String>?,
    val itineraryId: String?,
    val dynamicFields: Map<String, String>?,
    val specialNotes: String?,
    val paid: Boolean?,
    val embeddedMap: String?,
    val embeddedContent: String?,
    val files: Set<String>?,
    val commissionable: Boolean?,
    val dynamicPriceEnabled: Boolean?,
    val bookedByTa: Boolean?,
    val archived: Boolean?,
    val priceCurrency: CurrencyModel?,
    val costCurrency: CurrencyModel?,
    val hideOnProposal: Boolean?,
) {
    fun getChangedFields(proposalServiceModel: ProposalServiceModel): ProposalServiceModel =
        ProposalServiceModel(
            id = id,
            createdAt = createdAt,
            updatedAt = updatedAt,
            proposalId = proposalId,
            service = service,
            grouped = if (grouped != proposalServiceModel.grouped) proposalServiceModel.grouped else null,
            startDate = if (startDate != proposalServiceModel.startDate) proposalServiceModel.startDate else "",
            endDate = if (endDate.orEmpty() != proposalServiceModel.endDate.orEmpty()) proposalServiceModel.endDate else null,
            startTime = if (formatTimestampToHHMM(startTime.orEmpty()) !=
                formatTimestampToHHMM(proposalServiceModel.startTime.orEmpty())
            ) proposalServiceModel.startTime else null,
            endTime = if (formatTimestampToHHMM(endTime.orEmpty())
                != formatTimestampToHHMM(proposalServiceModel.endTime.orEmpty())
            ) proposalServiceModel.endTime else null,
            index = if (index != proposalServiceModel.index) proposalServiceModel.index else -1,
            additionalInfo =
                if (additionalInfo.orEmpty() != proposalServiceModel.additionalInfo.orEmpty()) {
                    proposalServiceModel.additionalInfo
                } else {
                    null
                },
            editedDescription =
                if (editedDescription.orEmpty() != proposalServiceModel.editedDescription.orEmpty()) {
                    proposalServiceModel.editedDescription
                } else {
                    null
                },
            price = if (price != proposalServiceModel.price) proposalServiceModel.price else null,
            cost = if (cost != proposalServiceModel.cost) proposalServiceModel.cost else null,
            status = if (status != proposalServiceModel.status) proposalServiceModel.status else null,
            internalNotes =
                if (internalNotes.orEmpty() != proposalServiceModel.internalNotes.orEmpty()) {
                    proposalServiceModel.internalNotes
                } else {
                    null
                },
            supplier = if (supplier != proposalServiceModel.supplier) proposalServiceModel.supplier else null,
            guestCount = if (guestCount != proposalServiceModel.guestCount) proposalServiceModel.guestCount else -1,
            updatedBy = if (updatedBy != proposalServiceModel.updatedBy) proposalServiceModel.updatedBy else null,
            clients = if (clients != proposalServiceModel.clients) proposalServiceModel.clients else null,
            dynamicFields =
                if (
                    dynamicFields == null &&
                    proposalServiceModel.dynamicFields.orEmpty().all { it.value.isEmpty() }
                ) {
                    null
                } else if (dynamicFields != proposalServiceModel.dynamicFields) {
                    proposalServiceModel.dynamicFields
                } else {
                    null
                },
            itineraryId = itineraryId,
            specialNotes = if (specialNotes.orEmpty() != proposalServiceModel.specialNotes.orEmpty()) proposalServiceModel.specialNotes else null,
            paid = if (paid != proposalServiceModel.paid) proposalServiceModel.paid else null,
            embeddedMap = if (embeddedMap.orEmpty() != proposalServiceModel.embeddedMap.orEmpty()) proposalServiceModel.embeddedMap else null,
            embeddedContent = if (embeddedContent.orEmpty() != proposalServiceModel.embeddedContent.orEmpty()) proposalServiceModel.embeddedContent else null,
            files = if (files != proposalServiceModel.files) proposalServiceModel.files else null,
            commissionable = if (commissionable != proposalServiceModel.commissionable) proposalServiceModel.commissionable else null,
            bookedByTa = if (bookedByTa != proposalServiceModel.bookedByTa) proposalServiceModel.bookedByTa else null,
            dynamicPriceEnabled = if (dynamicPriceEnabled != proposalServiceModel.dynamicPriceEnabled) proposalServiceModel.dynamicPriceEnabled else null,
            archived = if (archived != proposalServiceModel.archived) proposalServiceModel.archived else null,
            priceCurrency = if (priceCurrency?.code != proposalServiceModel.priceCurrency?.code) proposalServiceModel.priceCurrency else null,
            costCurrency = if (costCurrency?.code != proposalServiceModel.costCurrency?.code) proposalServiceModel.costCurrency else null,
            hideOnProposal = if (hideOnProposal != proposalServiceModel.hideOnProposal) proposalServiceModel.hideOnProposal else null
        )

    val isEmpty
        get() =
            this.copy(
                id = "",
                service = ServiceModel.empty,
                createdAt = "",
                updatedAt = null,
                proposalId = null,
                itineraryId = null,
            ) == empty

    enum class Status {
        Ready,
        POSent,
        Confirmed,
        TOChange,
        Cancelled,
        Completed,
    }

    companion object {
        val empty =
            ProposalServiceModel(
                id = "",
                proposalId = null,
                service = ServiceModel.empty,
                grouped = null,
                createdAt = "",
                updatedAt = null,
                additionalInfo = null,
                editedDescription = null,
                startDate = "",
                endDate = null,
                startTime = null,
                endTime = null,
                index = -1,
                price = null,
                status = null,
                internalNotes = null,
                supplier = null,
                guestCount = -1,
                updatedBy = null,
                clients = null,
                itineraryId = null,
                dynamicFields = null,
                specialNotes = null,
                paid = null,
                embeddedMap = null,
                embeddedContent = null,
                files = null,
                commissionable = null,
                bookedByTa = null,
                cost = null,
                archived = null,
                priceCurrency = null,
                costCurrency = null,
                dynamicPriceEnabled = null,
                hideOnProposal = null,
            )
    }
}